import { isEqual } from 'lodash';
import { Field, Form } from 'react-final-form';
import { useLocation, useNavigate } from 'hotel/navigation';

import {
  RosterRequestGroup,
  RosterRequestsFilterInput,
  User,
  UserRole,
  useUsersWithProfileQuery,
} from '../../generated/graphql';
import { useAppSelector } from '../../store/store';
import buildUserDisplayName from '../../utilities/buildUserDisplayName';
import getEnumLabel from '../../utilities/getEnumLabel';
import {
  getControlFieldLabelForPod,
  getGroupOptionsForPod,
} from '../../utilities/podBasedFiltering';
import { Button, TealButton } from '../Button';
import { TableFilterSelectField, TableFilterTextField } from '../Inputs';
import {
  TableFilterReactSelectEnum,
  formatEnumsToOptions,
  parseValuesToEnums,
} from '../Inputs/TableFilterInputs/ReactSelect/TableFilterReactSelectEnum';

type UserFormProps = {
  onSubmit: (values: RosterRequestsFilterInput) => void;
  initialFilters?: Partial<RosterRequestsFilterInput>;
  onClose: () => void;
};

export const getUserOption = (user: User) => {
  return <option value={user.id}>{buildUserDisplayName(user)}</option>;
};

const RequestFilterForm = ({
  onSubmit,
  initialFilters,
  onClose,
}: UserFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { rosterRequestStatus, rosterRequestType, rosterRequestGroups } =
    useAppSelector((state) => state.enums);
  const { pod: clientPod } = useAppSelector((state) => state.clientConfig);

  const { data: usersData } = useUsersWithProfileQuery();

  const submit = (values: RosterRequestsFilterInput) => {
    onSubmit(values);
    onClose();
  };

  return (
    <div className="mb-5">
      <Form
        initialValues={initialFilters}
        initialValuesEqual={(obj1, obj2) => isEqual(obj1, obj2)}
        onSubmit={submit}
        render={({ handleSubmit, form }) => (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-12 gap-x-2.5 gap-y-3.5">
              <div className="col-span-12 border-b border-gray-200 pb-5">
                <Field
                  labelText="Status"
                  name="status"
                  component={TableFilterReactSelectEnum}
                  options={rosterRequestStatus || []}
                  isMulti
                  isSearchable={false}
                  hideSelectedOptions={false}
                  parse={parseValuesToEnums}
                  format={formatEnumsToOptions(rosterRequestStatus || [])}
                />
              </div>
              <div className="col-span-12">
                <Field
                  labelText="Request Type"
                  name="requestType"
                  component={TableFilterReactSelectEnum}
                  options={rosterRequestType || []}
                  isMulti
                  isSearchable={false}
                  hideSelectedOptions={false}
                  parse={parseValuesToEnums}
                  format={formatEnumsToOptions(rosterRequestType || [])}
                />
              </div>
              <div className="col-span-12 grid grid-cols-12 gap-x-2.5 gap-y-3.5">
                <div className="col-span-6 mt-2 border-b">
                  <Field
                    placeholder="Enter ID"
                    labelText="Request ID"
                    name="id"
                    component={TableFilterTextField}
                  />
                </div>
                <div className="col-span-6 mt-2 border-b">
                  <Field
                    placeholder={`Enter ${getControlFieldLabelForPod(clientPod)}`}
                    labelText={getControlFieldLabelForPod(clientPod)}
                    name="controlNumber"
                    component={TableFilterTextField}
                  />
                </div>
              </div>

              <div className="col-span-12">
                <Field
                  labelText="Requested By"
                  name="requestedBy"
                  component={TableFilterSelectField}
                  options={
                    usersData?.users.filter(
                      (user) =>
                        user?.roles?.includes(UserRole.ClientUser) ||
                        user?.roles?.includes(UserRole.ClientAdmin),
                    ) ?? []
                  }
                  renderOption={(user: User) => getUserOption(user)}
                />
              </div>
              <div className="col-span-12">
                <Field
                  labelText="Group"
                  name="assignedGroup"
                  component={TableFilterSelectField}
                  options={getGroupOptionsForPod(clientPod)}
                  renderOption={(group: RosterRequestGroup) => {
                    return (
                      <option value={group}>
                        {getEnumLabel(rosterRequestGroups || [], group)}
                      </option>
                    );
                  }}
                />
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 mt-auto w-full border-t border-gray-200 bg-gray-200 p-4 shadow-lg sm:hidden">
              <div className="flex gap-3">
                <Button
                  className="w-full justify-center"
                  onClick={() => {
                    navigate(location.pathname, { replace: true });
                    form.reset();
                    onClose();
                  }}
                >
                  Clear Filters
                </Button>
                <TealButton
                  type="submit"
                  className="w-full justify-center text-sm"
                >
                  Apply Filters
                </TealButton>
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export default RequestFilterForm;
